import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { PageHeader } from '../components/PageHeader';
import { Plus, Edit2, Trash2, Search, Award, Download, Upload, FileSpreadsheet, CheckCircle2, AlertCircle } from 'lucide-react';
import { formatDate } from '../lib/format';
import { useToast } from '../hooks/use-toast';
import { EquipmentOptionLabel } from '../components/EquipmentCell';
import { Combobox } from '../components/Combobox';

const initialForm = {
  full_name: '', license_no: '', license_expiration: '', contact_no: '',
  is_available: true, assigned_vehicle_id: '',
  nc_ii_1: '', nc_ii_2: '', nc_ii_3: '',
  status: 'active',
};

const Operators = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [params, setParams] = useSearchParams();
  const [operators, setOperators] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [dropdowns, setDropdowns] = useState({ company_assigned: [], area: [], nc_ii: [] });
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(initialForm);
  // Bulk-upload state (mirrors Equipment page pattern).
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);

  const canEdit = hasRole(user, 'super_admin','admin','supervisor');
  const canDelete = hasRole(user, 'super_admin','admin');

  const load = useCallback(async () => {
    const r = await api.get('/operators');
    setOperators(r.data);
  }, []);
  const loadVehicles = useCallback(async (excludeOpId = '') => {
    const r = await api.get('/vehicles/available-for-operator', { params: { exclude_operator_id: excludeOpId || undefined } });
    setVehicles(r.data);
  }, []);
  const loadDropdowns = useCallback(async () => {
    const r = await api.get('/dropdowns');
    const grouped = {};
    for (const d of r.data) { grouped[d.category] = grouped[d.category] || []; grouped[d.category].push(d.value); }
    setDropdowns((p) => ({ ...p, ...grouped }));
  }, []);

  useEffect(() => { load(); loadDropdowns(); }, [load, loadDropdowns]);
  useEffect(() => { if (params.get('new') === '1' && canEdit) { openCreate(); params.delete('new'); setParams(params); } /* eslint-disable-next-line */ }, [params]);

  const openCreate = async () => { setEditing(null); setForm(initialForm); await loadVehicles(''); setOpen(true); };
  const openEdit = async (op) => {
    setEditing(op);
    const certs = op.nc_ii_certificates || [];
    setForm({
      ...initialForm,
      ...op,
      nc_ii_1: certs[0] || '',
      nc_ii_2: certs[1] || '',
      nc_ii_3: certs[2] || '',
      assigned_vehicle_id: op.assigned_vehicle_id || '',
    });
    await loadVehicles(op.id);
    setOpen(true);
  };

  const save = async () => {
    try {
      const certs = [form.nc_ii_1, form.nc_ii_2, form.nc_ii_3].map((c) => (c || '').trim()).filter(Boolean);

      // Auto-register any custom NC II value into dropdown_options
      const existingSet = new Set((dropdowns.nc_ii || []).map((x) => x.toLowerCase()));
      const customCerts = certs.filter((c) => !existingSet.has(c.toLowerCase()));
      for (const c of customCerts) {
        try { await api.post('/dropdowns', { category: 'nc_ii', value: c }); } catch (e) { console.warn('Auto-register nc_ii dropdown failed:', e?.message || e); }
      }

      const payload = {
        full_name: form.full_name,
        license_no: form.license_no || null,
        license_expiration: form.license_expiration || null,
        contact_no: form.contact_no || null,
        is_available: !!form.is_available,
        assigned_vehicle_id: form.assigned_vehicle_id || null,
        nc_ii_certificates: certs,
        status: form.status || 'active',
      };
      if (editing) await api.put(`/operators/${editing.id}`, payload);
      else await api.post('/operators', payload);

      if (customCerts.length > 0) {
        toast({ title: editing ? 'Operator updated' : 'Operator created', description: `Added ${customCerts.length} new NC II certification${customCerts.length > 1 ? 's' : ''} to the catalog.` });
      } else {
        toast({ title: editing ? 'Operator updated' : 'Operator created' });
      }
      setOpen(false); load(); loadDropdowns();
    } catch (e) {
      toast({ title: 'Error', description: e.response?.data?.detail || 'Failed', variant: 'destructive' });
    }
  };

  const remove = async (op) => {
    if (!window.confirm(`Delete ${op.full_name}?`)) return;
    await api.delete(`/operators/${op.id}`); toast({ title: 'Deleted' }); load();
  };

  const exportCsv = () => {
    if (operators.length === 0) { toast({ title: 'Nothing to export', variant: 'destructive' }); return; }
    const headers = ['Full Name', 'License Number', 'License Expiration', 'Contact Information', 'Availability', 'Assigned Equipment Code', 'NC II Certificate 1', 'NC II Certificate 2', 'NC II Certificate 3', 'Status'];
    const escape = (v) => {
      const s = v === null || v === undefined ? '' : String(v);
      if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
      return s;
    };
    const rows = operators.map((o) => {
      const certs = o.nc_ii_certificates || [];
      return [
        o.full_name, o.license_no, o.license_expiration, o.contact_no,
        o.status === 'active' ? (o.is_available ? 'Available' : 'Assigned') : 'Archived',
        o.assigned_vehicle_code || '',
        certs[0] || '', certs[1] || '', certs[2] || '',
        o.status === 'active' ? 'Active' : 'Archived',
      ].map(escape).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    a.href = url; a.download = `operators_${ts}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
    toast({ title: 'Exported', description: `${operators.length} operator${operators.length !== 1 ? 's' : ''} exported.` });
  };

  const filtered = operators.filter((o) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return ['full_name','employee_no','license_no','contact_no','assigned_vehicle_code','area'].some((k) => String(o[k] || '').toLowerCase().includes(s));
  });

  const onUploadFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const name = f.name.toLowerCase();
    if (!name.endsWith('.xlsx') && !name.endsWith('.xls') && !name.endsWith('.csv')) {
      toast({ title: 'Invalid file', description: 'Only .xlsx, .xls or .csv files are accepted', variant: 'destructive' });
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast({ title: 'File too large', description: 'Maximum 5MB', variant: 'destructive' });
      return;
    }
    setUploadFile(f);
    setUploadResult(null);
  };
  const submitUpload = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadResult(null);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      const r = await api.post('/operators-upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setUploadResult(r.data);
      const created = r.data.created || 0;
      const skipped = r.data.skipped?.length || 0;
      const errors = r.data.errors?.length || 0;
      if (created > 0) {
        toast({ title: 'Upload complete', description: `${created} added, ${skipped} skipped, ${errors} errors` });
      } else {
        toast({ title: 'Upload finished with issues', description: `${errors} errors, ${skipped} skipped`, variant: 'destructive' });
      }
      load();
    } catch (e) {
      toast({ title: 'Upload failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };
  const downloadTemplate = async () => {
    try {
      const r = await api.get('/operators-template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'operators_template.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast({ title: 'Download failed', description: e.response?.data?.detail || 'Server error', variant: 'destructive' });
    }
  };
  const resetUpload = () => { setUploadFile(null); setUploadResult(null); };

  return (
    <div className="space-y-4">
      <PageHeader title="Operator Management" description="Manage operators, licenses, and equipment assignments."
        actions={<>
          <Button variant="outline" onClick={exportCsv}><Download className="h-4 w-4 mr-1.5" /> Export CSV</Button>
          {canEdit && <Button variant="outline" onClick={() => setUploadOpen(true)} data-testid="operators-upload-btn"><Upload className="h-4 w-4 mr-1.5" /> Upload</Button>}
          {canEdit && <Button onClick={openCreate} className="bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]"><Plus className="h-4 w-4 mr-1.5" /> Add Operator</Button>}
        </>} />

      <Card>
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input className="pl-9" placeholder="Search by name, license, employee no..." value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b text-left text-slate-600">
              <tr>
                <th className="p-3">Operator</th><th className="p-3">License #</th>
                <th className="p-3">License Exp.</th><th className="p-3">NC II Certificates</th><th className="p-3">Assigned Equipment</th>
                <th className="p-3">Availability</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {filtered.length === 0 ? <tr><td colSpan={8} className="p-8 text-center text-slate-500">No operators found.</td></tr> :
                filtered.map((o) => (
                  <tr key={o.id} className="table-row-hover">
                    <td className="p-3">
                      <div className="flex items-center gap-2">
                        <div className="h-8 w-8 rounded-full bg-[hsl(217,91%,24%)] text-white grid place-items-center text-xs font-bold">{o.full_name.split(' ').map(s=>s[0]).slice(0,2).join('')}</div>
                        <div>
                          <div className="font-medium">{o.full_name}</div>
                          <div className="text-xs text-slate-500">{o.contact_no}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 font-mono text-xs">{o.license_no}</td>
                    <td className="p-3 text-xs">{formatDate(o.license_expiration)}</td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1 max-w-xs">
                        {(o.nc_ii_certificates || []).map((c) => <Badge key={c} variant="secondary" className="text-[10px]"><Award className="h-3 w-3 mr-1" />{c.replace('Heavy Equipment Operation ', '')}</Badge>)}
                      </div>
                    </td>
                    <td className="p-3">
                      {o.assigned_vehicle_code ? (
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="font-mono font-semibold text-xs">{o.assigned_vehicle_code}</span>
                          {o.assigned_vehicle_category && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-[hsl(217,91%,24%)] border border-blue-200">{o.assigned_vehicle_category}</span>}
                        </div>
                      ) : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary" className={o.is_available ? 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100' : 'bg-amber-100 text-amber-800 hover:bg-amber-100'}>
                        {o.is_available ? 'Available' : 'Unavailable'}
                      </Badge>
                    </td>
                    <td className="p-3">
                      <Badge variant={o.status === 'active' ? 'default' : 'secondary'} className={o.status === 'active' ? 'bg-blue-100 text-[hsl(217,91%,24%)] hover:bg-blue-100' : ''}>
                        {o.status === 'active' ? 'Active' : 'Archived'}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      {canEdit && <Button size="sm" variant="ghost" onClick={() => openEdit(o)}><Edit2 className="h-4 w-4" /></Button>}
                      {canDelete && <Button size="sm" variant="ghost" onClick={() => remove(o)}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Dialog open={uploadOpen} onOpenChange={(o) => { if (!uploading) { setUploadOpen(o); if (!o) resetUpload(); } }}>
        <DialogContent className="max-w-lg" data-testid="operators-upload-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Upload Operators</DialogTitle>
            <DialogDescription>Upload an .xlsx or .csv following the template. Duplicates (same name + license) are skipped.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="operators-download-template-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" /> Download template
            </Button>
            <div>
              <Input type="file" accept=".xlsx,.xls,.csv" onChange={onUploadFileChange} data-testid="operators-upload-file" />
              {uploadFile && <div className="mt-1 text-[11px] text-slate-500">Selected: {uploadFile.name} ({(uploadFile.size / 1024).toFixed(1)} KB)</div>}
            </div>
            {uploadResult && (
              <div className="rounded-md border p-3 text-sm space-y-2" data-testid="operators-upload-result">
                <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Added: <b>{uploadResult.created}</b> of {uploadResult.total_rows}</div>
                {(uploadResult.skipped?.length || 0) > 0 && (
                  <div className="text-amber-700">
                    <div className="font-semibold">Skipped ({uploadResult.skipped.length}):</div>
                    <ul className="pl-4 list-disc text-[12px] max-h-32 overflow-y-auto">
                      {uploadResult.skipped.slice(0, 20).map((s, i) => <li key={i}>Row {s.row}: {s.operator || ''} · {s.reason}</li>)}
                    </ul>
                  </div>
                )}
                {(uploadResult.errors?.length || 0) > 0 && (
                  <div className="text-red-700">
                    <div className="font-semibold flex items-center gap-1"><AlertCircle className="h-4 w-4" /> Errors ({uploadResult.errors.length}):</div>
                    <ul className="pl-4 list-disc text-[12px] max-h-32 overflow-y-auto">
                      {uploadResult.errors.slice(0, 20).map((e, i) => <li key={i}>Row {e.row}: {e.operator || ''} · {e.reason}</li>)}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setUploadOpen(false); resetUpload(); }} disabled={uploading}>Close</Button>
            <Button onClick={submitUpload} disabled={!uploadFile || uploading} className="bg-emerald-600 hover:bg-emerald-700" data-testid="operators-upload-submit-btn">
              {uploading ? 'Uploading…' : <><Upload className="h-4 w-4 mr-1.5" /> Upload</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl">{editing ? 'Edit Operator' : 'Add New Operator'}</DialogTitle>
            <DialogDescription className="sr-only">Operator details form.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="font-semibold">Full Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Enter operator full name" />
            </div>
            <div>
              <Label className="font-semibold">License Number</Label>
              <Input value={form.license_no || ''} onChange={(e) => setForm({ ...form, license_no: e.target.value })} placeholder="Enter license number" />
            </div>
            <div>
              <Label className="font-semibold">License Expiration Date</Label>
              <Input type="date" value={form.license_expiration || ''} onChange={(e) => setForm({ ...form, license_expiration: e.target.value })} />
            </div>
            <div>
              <Label className="font-semibold">Contact Information</Label>
              <Input value={form.contact_no || ''} onChange={(e) => setForm({ ...form, contact_no: e.target.value })} placeholder="Phone number or email" />
            </div>
            <div>
              <Label className="font-semibold">Availability</Label>
              <Select value={form.is_available ? 'available' : 'unavailable'} onValueChange={(v) => setForm({ ...form, is_available: v === 'available' })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Available</SelectItem>
                  <SelectItem value="unavailable">Unavailable</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="font-semibold">Assigned Equipment Code <span className="text-slate-400 font-normal">(Optional)</span></Label>
              <Select value={form.assigned_vehicle_id || '__none__'} onValueChange={(v) => setForm({ ...form, assigned_vehicle_id: v === '__none__' ? '' : v })}>
                <SelectTrigger><SelectValue placeholder="No equipment assigned" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No equipment assigned</SelectItem>
                  {vehicles.map((v) => <SelectItem key={v.id} value={v.id}><span className="font-mono font-semibold">{v.equipment_code}</span> {v.category && <span className="text-[10px] ml-1 px-1.5 py-0.5 rounded bg-blue-50 text-[hsl(217,91%,24%)] border border-blue-200">{v.category}</span>} <span className="text-slate-500">— {v.description || ''}</span></SelectItem>)}
                </SelectContent>
              </Select>
              <div className="text-xs text-slate-500 mt-1">Only shows equipment with "Operational" status that is not currently assigned to another operator</div>
            </div>
            {[1, 2, 3].map((n) => (
              <div key={n}>
                <Label className="font-semibold">
                  NC II Certificate {n}
                  {n > 1 && <span className="text-slate-400 font-normal"> (Optional)</span>}
                </Label>
                <Combobox
                  value={form[`nc_ii_${n}`] || ''}
                  onChange={(v) => setForm({ ...form, [`nc_ii_${n}`]: v })}
                  options={dropdowns.nc_ii || []}
                  placeholder="Select from list or type to add new..."
                />
                <div className="text-[10px] text-slate-400 mt-0.5">You can pick from saved certifications or type a new one — it will be saved automatically.</div>
              </div>
            ))}
            <div>
              <Label className="font-semibold">Status</Label>
              <Select value={form.status || 'active'} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="archived">Archived</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={!form.full_name?.trim()} className="bg-emerald-500 hover:bg-emerald-600 text-white">{editing ? 'Update' : 'Create'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Field = ({ label, value, onChange, type = 'text' }) => (<div><Label>{label}</Label><Input type={type} value={value || ''} onChange={(e) => onChange(e.target.value)} /></div>);
const SelectField = ({ label, value, onChange, options }) => {
  const opts = options.map((o) => typeof o === 'string' ? { value: o, label: o } : o);
  return (
    <div>
      <Label>{label}</Label>
      <Select value={value || ''} onValueChange={onChange}>
        <SelectTrigger><SelectValue placeholder="Select..." /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
};

export default Operators;
